import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// API endpoints
const CATEGORIES_URL = 'api/v1/category';

// Async thunks
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(CATEGORIES_URL + '/all');
      return response.data.data.results;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const addCategory = createAsyncThunk(
  'categories/addCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("name", categoryData.name);
      formData.append("isActive", categoryData.isActive);
      formData.append("fulfillmentType", categoryData.fulfillmentType || "on_site");
      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }

      const response = await api.post(CATEGORIES_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("isActive", data.isActive);
      formData.append("fulfillmentType", data.fulfillmentType || "on_site");
      if (data.image) {
        formData.append("image", data.image);
      }

      const response = await api.put(`${CATEGORIES_URL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${CATEGORIES_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete category');
    }
  }
);

const initialState = {
  categories: [],
  loading: false,
  error: null,
  addCategoryLoading: false,
  addCategoryError: null,
  updateCategoryLoading: false,
  updateCategoryError: null,
  deleteCategoryLoading: false,
  deleteCategoryError: null,
};

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.addCategoryError = null;
      state.updateCategoryError = null;
      state.deleteCategoryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add category
      .addCase(addCategory.pending, (state) => {
        state.addCategoryLoading = true;
        state.addCategoryError = null;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.addCategoryLoading = false;
        state.categories.push(action.payload);
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.addCategoryLoading = false;
        state.addCategoryError = action.payload;
      })

      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.updateCategoryLoading = true;
        state.updateCategoryError = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.updateCategoryLoading = false;
        const index = state.categories.findIndex(cat => cat._id === action.payload._id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.updateCategoryLoading = false;
        state.updateCategoryError = action.payload;
      })

      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.deleteCategoryLoading = true;
        state.deleteCategoryError = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.deleteCategoryLoading = false;
        state.categories = state.categories.filter(cat => cat._id !== action.payload);
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.deleteCategoryLoading = false;
        state.deleteCategoryError = action.payload;
      });
  },
});

export const { clearErrors } = categoriesSlice.actions;
export default categoriesSlice.reducer; 